import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { ensureArtistSetup, ensurePricingSettings } from "@/lib/ensureArtistSetup";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signUp: (
    email: string,
    password: string,
    artistName: string,
    redirectUrl?: string,
  ) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Set up auth state listener FIRST
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);

      // For OAuth sign-ins (Google etc.) there is no explicit signUp() call,
      // so we must ensure the artist_profiles + pricing_settings rows exist here.
      // Email signups are handled synchronously in signUp() below.
      if (
        event === "SIGNED_IN" &&
        session?.user &&
        session.user.app_metadata?.provider !== "email"
      ) {
        const u = session.user;
        const name =
          u.user_metadata?.full_name ||
          u.user_metadata?.name ||
          u.email?.split("@")[0] ||
          "Artist";
        // Fire-and-forget — errors are logged inside ensureArtistSetup
        ensureArtistSetup(u.id, name).catch(console.error);
      }
    });

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return { error: error as Error | null };
  };

  // ── BUG 1 FIX: redirectUrl parameter toegevoegd ──
  // Auth.tsx stuurt nu de claim-URL mee zodat de e-mailbevestiging
  // de gebruiker terugbrengt naar /claim?studio=... in plaats van /
  const signUp = async (email: string, password: string, artistName: string, redirectUrl?: string) => {
    const finalRedirectUrl = redirectUrl || `${window.location.origin}/`;

    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: finalRedirectUrl,
        data: {
          artist_name: artistName,
        },
      },
    });

    if (error) {
      return { error: error as Error };
    }

    // Create artist profile + pricing_settings + default multipliers after signup.
    // If email confirmation is required, data.user is set but the session is null —
    // we still write the profile row so it's ready when the user confirms.
    if (data.user) {
      try {
        const { data: profile, error: profileError } = await supabase
          .from("artist_profiles")
          .insert({ user_id: data.user.id, artist_name: artistName })
          .select("id")
          .single();

        if (profileError) {
          console.error("Error creating artist profile:", profileError);
        } else if (profile?.id) {
          await ensurePricingSettings(profile.id);
        }
      } catch (e) {
        console.error("Error during post-signup setup:", e);
      }
    }

    return { error: null };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, signIn, signUp, signOut }}>{children}</AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
